package org.example.project.enginepersonal

import org.example.project.settings.UISettings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.coroutines.await
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text
import kotlin.js.Promise

@Composable
fun StempelkarteScreen() {
    val user = LoginService.getUser()
    val refreshVersion = maxOf(
        LocalScreenRefreshVersion.current,
        GlobalManager.jsonDomainVersion("user"),
        GlobalManager.jsonDomainVersion("personal")
    )
    var qrCode by remember { mutableStateOf("") }
    var name by remember { mutableStateOf(stempelkarteUserName(user)) }
    var message by remember { mutableStateOf("") }

    LaunchedEffect(user?.id, user?.firmenId, refreshVersion) {
        val userId = LoginService.getId().ifBlank { user?.id.orEmpty() }
        if (userId.isBlank()) {
            message = "Kein Benutzer angemeldet."
            return@LaunchedEffect
        }
        val akte = PersonalakteApi.loadAkte(userId)
        name = akte.qrLabel.ifBlank { stempelkarteUserName(user) }
        val qrText = akte.qrCodeText.ifBlank { stempelkarteQrText(akte.copy(userId = userId), user) }
        qrCode = akte.qrCode.ifBlank {
            runCatching { stempelkarteQrCodeDataUrl(qrText) }.getOrDefault("")
        }
        message = if (qrCode.isBlank()) "QR Code konnte nicht geladen werden." else ""
    }

    Style(StempelkarteStyles)
    Div({ classes(StempelkarteStyles.page) }) {
        Div({ classes(StempelkarteStyles.card) }) {
            if (qrCode.isNotBlank()) {
                Img(src = qrCode, attrs = { classes(StempelkarteStyles.qrImage) })
            } else {
                Div({ classes(StempelkarteStyles.qrPlaceholder) }) { Text("QR") }
            }
            Span({ classes(StempelkarteStyles.name) }) { Text(name.ifBlank { "-" }) }
            if (message.isNotBlank()) {
                Span({ classes(StempelkarteStyles.message) }) { Text(message) }
            }
        }
    }
}

private fun stempelkarteUserName(user: All?): String =
    user?.name?.ifBlank {
        listOf(user.vorname, user.nachname).filter { it.isNotBlank() }.joinToString(" ")
    }.orEmpty()

private fun stempelkarteQrText(akte: PersonalAkte, user: All?): String =
    buildJsonObject {
        put("typ", "lumatica-personal-stempel")
        put("firma", akte.vertrag["firma"].orEmpty().ifBlank { user?.firma.orEmpty() })
        put("standort", akte.vertrag["standort"].orEmpty().ifBlank { user?.standort.orEmpty() })
        put("personalnummer", akte.vertrag["personalnummer"].orEmpty().ifBlank { user?.personalnummer.orEmpty() })
        put("userId", akte.userId.ifBlank { user?.id.orEmpty() })
        put("vorname", akte.personal["vorname"].orEmpty().ifBlank { user?.vorname.orEmpty() })
        put("nachname", akte.personal["nachname"].orEmpty().ifBlank { user?.nachname.orEmpty() })
    }.toString()

private suspend fun stempelkarteQrCodeDataUrl(qrText: String): String {
    if (qrText.isBlank()) return ""
    val qrcode = js("typeof require !== 'undefined' ? require('qrcode') : null")
    val module = qrcode?.default ?: qrcode ?: error("qrcode not available")
    return module.toDataURL(qrText).unsafeCast<Promise<String>>().await()
}

private object StempelkarteStyles : StyleSheet() {
    val page by style {
        width(100.percent)
        height(100.percent)
        minHeight(0.px)
        boxSizing("border-box")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        padding(18.px)
        backgroundColor(Color.transparent)
        color(Color.white)
        overflow("hidden")
    }
    val card by style {
        width(100.percent)
        maxWidth(420.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        gap(18.px)
    }
    val qrImage by style {
        width(82.vw)
        maxWidth(360.px)
        height(82.vw)
        maxHeight(360.px)
        borderRadius(8.px)
        backgroundColor(Color.white)
        padding(12.px)
        boxSizing("border-box")
    }
    val qrPlaceholder by style {
        width(82.vw)
        maxWidth(360.px)
        height(82.vw)
        maxHeight(360.px)
        borderRadius(8.px)
        border(2.px, LineStyle.Solid, rgba(255, 255, 255, 0.4))
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        color(rgba(255, 255, 255, 0.6))
        fontSize(42.px)
        fontWeight(900)
        boxSizing("border-box")
    }
    val name by style {
        width(100.percent)
        color(Color.white)
        fontSize(26.px)
        fontWeight(900)
        textAlign("center")
        lineHeight("1.2")
    }
    val message by style {
        color(UISettings.ACCENT)
        fontSize(14.px)
        fontWeight(800)
        textAlign("center")
    }
}
